import { message } from "antd"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { AiOutlineClose } from "react-icons/ai"
import styled from "styled-components"

import { LoadingOutlined } from "@ant-design/icons"

import * as api from "../../../../api"
import { useStateValue } from "../../context/AppStateStore"
import getAgeFromFiscalCode, { getIsFemaleFromFiscalCode } from "../../toolkit"
import AppButton from "../AppButton"
import AppInput from "../FormItems/AppInput"
import Paragraph from "../Typography/Paragraph"
import ScreenTitle from "../Typography/ScreenTitle"

// ----------------------------------------------------------------------------

function _LoginOverlay(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, targetEvent, onSuccess, onCancel, loading } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [code, setCode] = useState()
    const [, dispatch] = useStateValue()

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function handleAuth() {
        try {
            const age = getAgeFromFiscalCode(code)
            const isFemale = getIsFemaleFromFiscalCode(code)

            const minAge = targetEvent?.eventType?.minAge ?? 35
            const maxAge = targetEvent?.eventType?.maxAge ?? 49

            if (!isFemale) {
                message.error(
                    "Devi essere di sesso femminile per partecipare alle nostre proposte."
                )
                return
            }

            if (age >= minAge && age <= maxAge) {
                const { user, token } = await api.validateAndCreateUser(code)
                dispatch({
                    type: "SIGN_IN_FULFILLED",
                    payload: { user, token },
                })
                onSuccess && onSuccess({ user, token })
            } else
                message.error(
                    `L'età non corrisponde ai requisiti richiesti per partecipare all'evento.`
                )
        } catch {
            message.error("Inserire un codice fiscale valido.")
        }
    }

    function handleChange(newCode) {
        setCode(newCode?.target?.value?.toUpperCase())
    }

    function renderOverlayContent() {
        return (
            <div className="overlay-content">
                <div className="form-wrapper">
                    {loading ? (
                        <div className="loading">
                            <LoadingOutlined />
                        </div>
                    ) : (
                        <>
                            {onCancel && (
                                <button
                                    className="close-btn"
                                    type="button"
                                    onClick={onCancel}
                                >
                                    <AiOutlineClose />
                                </button>
                            )}
                            <ScreenTitle
                                className="screen-title"
                                text="Controllo codice fiscale"
                            />

                            {targetEvent && (
                                <Paragraph className="paragraph">
                                    {`Questo evento richiede che la tua età sia compresa tra ${targetEvent?.eventType?.minAge || 35
                                        } e ${targetEvent?.eventType?.maxAge || 49
                                        } anni. Inserisci il tuo codice fiscale per verificare la tua età.`}
                                </Paragraph>
                            )}
                            <div className="code-form">
                                <div className="row">
                                    <div className="input">
                                        <AppInput
                                            label="Codice fiscale"
                                            value={code}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <AppButton
                                        className="submit-btn"
                                        uxType="primary"
                                        htmlType="button"
                                        onClick={handleAuth}
                                    >
                                        Invia
                                    </AppButton>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <div className="children-wrapper">{props.children}</div>
            <div className="mask"></div>
            {renderOverlayContent()}
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_LoginOverlay.propTypes = {
    className: PropTypes.string.isRequired,
    children: PropTypes.any,
    targetEvent: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
    loading: PropTypes.any,
}

_LoginOverlay.defaultProps = {}

// ----------------------------------------------------------------------------

const LoginOverlay = styled(_LoginOverlay)`
    & {
        .mask {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            backdrop-filter: blur(10px);
        }
        .children-wrapper {
        }
        .overlay-content {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding-top: 80px;
            flex-direction: column;
            position: absolute;
            top: 0;
            left: 20px;
            right: 20px;

            @media (max-width: 500px) {
                .form-wrapper {
                    .code-form {
                        .row {
                            flex-direction: column !important;
                            gap: 15px;
                            .input,
                            .submit-btn {
                                width: 100% !important;
                                min-width: 100% !important;
                            }
                        }
                    }
                }
            }

            .form-wrapper {
                background: rgba(0, 0, 0, 0.15);
                /* border: 1px solid black; */
                padding: 20px;
                border-radius: 16px;
                position: relative;
                /* box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.2); */

                .loading {
                    width: 60vw;
                    height: 200px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    svg {
                        width: 40px;
                        height: 40px;
                        color: ${({ theme }) =>
        theme.colors.buttonBackground} !important;
                    }
                }

                .close-btn {
                    position: absolute;
                    top: 5px;
                    right: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border: none;
                    background: transparent;
                    cursor: pointer;

                    svg {
                        width: 100%;
                        height: 100%;
                    }
                }

                .screen-title {
                    font-size: 22px;
                    padding-top: 10px;
                    margin-top: 0;
                }
                .paragraph {
                    font-size: 14px;
                    text-align: center;
                    color: black;
                }
                .code-form {
                    height: 160px;
                    margin-top: 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: center;
                    .label {
                        font-size: 14px;
                        font-family: "Poppins-Bold";
                        text-transform: uppercase;
                    }
                    .row {
                        width: 100%;
                        display: flex;
                        flex-direction: row;
                        align-items: flex-end;
                        .input {
                            display: flex;
                            flex-grow: 1;
                            input {
                                width: 100%;
                                background: white;
                            }
                        }
                        .submit-btn {
                            min-width: 150px;
                            margin-left: 17px;
                        }
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default LoginOverlay
